import { useEffect, useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { X, ArrowUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SpeechState } from './types';

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const TICK_MS = 80;
const DOT_THRESHOLD = 0.15;
const WAVE_COLOR = '#0f172a'; // slate-900 — matches the send button, not the mic's idle color

/**
 * Simulated (not audio-reactive) scrolling waveform: a rolling buffer of
 * pseudo-random amplitudes, drawn as dots during quiet stretches and bars during
 * "speech," oldest data aging off the left edge as new values enter on the
 * right — the same read as a real recorder's waveform without needing an
 * AnalyserNode. Real amplitude would only be available for the AssemblyAI
 * provider (raw PCM) and not Web Speech (no raw-audio access at all), which
 * would make the two providers look inconsistent — simulated looks identical
 * for both and needs no audio-pipeline plumbing.
 */
function Waveform({ active }: { active: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    let buffer: number[] = [];
    let amplitude = 0.05;
    let lastTick = 0;
    let rafId: number;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const nextAmplitude = (): number => {
      if (Math.random() < 0.05) {
        return Math.max(0.03, amplitude * 0.3); // the occasional natural pause
      }
      const target = 0.15 + Math.random() * 0.85;
      return amplitude + (target - amplitude) * 0.5;
    };

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      const { width, height } = rect;
      const barCount = Math.max(1, Math.floor(width / (BAR_WIDTH + BAR_GAP)));

      if (buffer.length > barCount) {
        buffer = buffer.slice(buffer.length - barCount);
      }

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = WAVE_COLOR;
      const midY = height / 2;

      // Anchored to the right edge, growing leftward as the buffer fills — the
      // newest sample is always at the right, nothing is drawn for the rest of
      // the row until there's real history to show. Once the buffer reaches
      // barCount it behaves as a true scroll: the oldest sample drops off the
      // left in `tick` below as a new one is pushed on the right.
      const startX = width - buffer.length * (BAR_WIDTH + BAR_GAP);

      buffer.forEach((amp, i) => {
        const x = startX + i * (BAR_WIDTH + BAR_GAP);
        if (amp < DOT_THRESHOLD) {
          const r = BAR_WIDTH / 2;
          ctx.beginPath();
          ctx.arc(x + r, midY, r, 0, Math.PI * 2);
          ctx.fill();
          return;
        }
        const barHeight = Math.max(4, amp * height * 0.9);
        const y = midY - barHeight / 2;
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, y, BAR_WIDTH, barHeight, BAR_WIDTH / 2);
        } else {
          ctx.rect(x, y, BAR_WIDTH, barHeight);
        }
        ctx.fill();
      });
    };

    const tick = (timestamp: number) => {
      // No permission yet / not actually listening (still connecting, or
      // finalizing after stop) — freeze instead of faking activity on audio
      // that was never captured. Only advances the buffer while truly listening.
      if (activeRef.current && timestamp - lastTick >= TICK_MS) {
        lastTick = timestamp;
        amplitude = nextAmplitude();
        buffer.push(amplitude);
        const barCount = Math.max(1, Math.floor(canvas.getBoundingClientRect().width / (BAR_WIDTH + BAR_GAP)));
        if (buffer.length > barCount) buffer.shift();
      }
      draw();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="w-full h-6" aria-hidden="true" />;
}

interface VoiceRecordingBarProps {
  /** Caller only ever renders this for 'starting' | 'listening' | 'finalizing' —
   *  typed as the full `SpeechState` because TS can't narrow a property access
   *  through the caller's `isIdle` boolean, not because 'idle'/'error' are
   *  meaningful here. */
  state: SpeechState;
  /** Live transcript (committed + in-flight partial) — see `useSpeechInput`'s
   *  `onPartial` wiring. Read-only here: it's being written programmatically
   *  by the ASR stream, so letting the user type into it mid-recording would
   *  race with the next partial overwriting their edit. */
  value: string;
  onCancel: () => void;
  /** Stops the session and sends the transcript, in one action — there is no
   *  separate "stop and review" step here (see `voice-input-ui-preferences`
   *  project memory: the user explicitly asked to collapse the previous
   *  Stop+Send pair into a single button). Cancel is still separate, since
   *  it discards instead of sending. */
  onSend: () => void;
}

/**
 * Replaces the entire input row while recording — mirrors the reference mobile
 * apps (Claude, ChatGPT). Two rows, not one: the growing transcript gets the
 * full row width on top (cramming it into the same row as the controls left
 * almost no room for text on a phone screen and forced aggressive
 * word-wrapping), and the waveform — still the "I'm receiving audio" signal —
 * lives in the control row underneath, alongside Cancel and a single combined
 * stop-and-send button.
 */
export const VoiceRecordingBar = ({ state, value, onCancel, onSend }: VoiceRecordingBarProps) => {
  const isBusy = state === 'finalizing';

  return (
    <div className="flex flex-col gap-2 w-full">
      <TextareaAutosize
        value={value}
        readOnly
        placeholder="Listening…"
        // Matches the idle textarea's text-base (iOS auto-zoom guard) and
        // horizontal padding; full width now, no competing flex siblings.
        className="w-full resize-none max-h-[200px] bg-background px-3 focus:outline-none text-base"
        minRows={1}
      />

      <div className="flex items-center gap-2 w-full">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Cancel recording"
          disabled={isBusy}
          onClick={onCancel}
          className="h-9 w-9 shrink-0 rounded-full cursor-pointer"
        >
          <X className="h-4 w-4" />
        </Button>

        <div className="flex-1 h-9 flex items-center" aria-live="polite" aria-label="Listening">
          <Waveform active={state === 'listening'} />
        </div>

        <Button
          type="button"
          size="icon"
          aria-label="Stop and send"
          disabled={isBusy}
          onClick={onSend}
          className="h-9 w-9 shrink-0 rounded-full cursor-pointer bg-slate-900 text-white hover:bg-slate-800"
        >
          {isBusy ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
        </Button>
      </div>
    </div>
  );
};
